/* eslint-disable react/no-unstable-nested-components */
import lightFormat from "date-fns/lightFormat";
import getYear from "date-fns/getYear";
import differenceInYears from "date-fns/differenceInYears";
import numeral from "numeral";
import ClipLoader from "react-spinners/ClipLoader";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { FormattedMessage, useIntl } from "react-intl";

import classNames from "components/ui/classNames";
import { ScrollArea } from "components/ui/ScrollArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";

import type { IDividendsPaidDetails } from "types/graphqlTypes";
import parseDateYmd from "lib/utils/parseDateYmd";
import { palette } from "lib/miolo";

interface IDividendsPaidTabContentProps {
  dividendsPaid: IDividendsPaidDetails[];
  availableYears: number[];
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
}

export default function DividendsPaidTabContent({
  dividendsPaid,
  availableYears,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
}: IDividendsPaidTabContentProps) {
  const intl = useIntl();
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();

  const columns = [
    columnHelper.accessor("dividendType", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Tipo" id="wt+XcA" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>
            {info.getValue() === "JUROS" ? "JCP" : <FormattedMessage defaultMessage="Dividendo" id="/E+MJo" />}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("recordDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Com" id="uJFyej" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{lightFormat(parseDateYmd(info.getValue()), "dd/MM/yyyy")}</span>
        </div>
      ),
    }),
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{getExcludingDate(parseDateYmd(info.row.original.recordDate), parseDateYmd(info.getValue()))}</span>
        </div>
      ),
    }),
    columnHelper.accessor("payDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{info.getValue() ? getPayDate(parseDateYmd(info.getValue())) : "-"}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amountPaid", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Valor (R$)" id="egpnv2" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{numeral(info.getValue()).format("0.00").replace(".", ",")}</span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: dividendsPaid,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-full md:w-52">
        <Select
          value={filterDividendsByYear ? String(filterDividendsByYear) : ""}
          onValueChange={(value) => {
            if (!value) {
              return setFilterDividendsByYear(null);
            }

            return setFilterDividendsByYear(parseInt(value, 10));
          }}
        >
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "w-full rounded-lg",
              "border border-slate-200 bg-white",
              "text-sm font-medium tracking-wide text-slate-600",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o ano",
                id: "0HA0Sr",
              })}
            />
          </SelectTrigger>
          <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
            <SelectItem value="">
              {filterDividendsByYear === null
                ? intl.formatMessage({
                    defaultMessage: "Filtre o ano",
                    id: "0HA0Sr",
                  })
                : intl.formatMessage({
                    defaultMessage: "Remover filtro",
                    id: "J5Id85",
                  })}
            </SelectItem>

            {availableYears.map((year) => (
              <SelectItem key={`selectBoxYear/${year}`} value={String(year)} className="w-full">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex w-full items-center justify-center">
          <ClipLoader size={28} color={palette.royalBlue} loading={true} />
        </div>
      )}

      {!loading && (
        <ScrollArea type="always" orientation="horizontal" className="relative w-full">
          <div className="pb-4 lg:w-full">
            <div
              className={classNames(
                "relative overflow-auto rounded-xl border border-slate-200",
                "dark:border-slate-950",
              )}
            >
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                    >
                      {headerGroup.headers
                        .filter((header) => !header.column.columnDef.meta?.isHidden)
                        .map((header) => {
                          return (
                            <th
                              key={header.id}
                              className={classNames(
                                "m-0 p-0 text-center",
                                header.column.columnDef.meta?.header?.className,
                              )}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                          );
                        })}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-slate-200 font-mono dark:divide-slate-700">
                  {table.getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                        {row
                          .getVisibleCells()
                          .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                          .map((cell) => {
                            return (
                              <td
                                key={cell.id}
                                className={classNames(
                                  "m-0 p-0 text-center",
                                  cell.column.columnDef.meta?.cell?.className,
                                )}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function getExcludingDate(recordDate: Date, excludingDate: Date) {
  const difference = differenceInYears(excludingDate, recordDate);

  if (difference >= 2) {
    return "-";
  }

  return lightFormat(excludingDate, "dd/MM/yyyy");
}

function getPayDate(payDate: Date) {
  if (getYear(payDate) === 1900) {
    return "-";
  }

  return lightFormat(payDate, "dd/MM/yyyy");
}
